<template>
    <input
        :id="inputId"
        ref="textarea"
        :name="inputName"
        :value="value"
        :rows="rows"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :maxlength="maxLength"
        :spellcheck="spellcheck"
        :aria-label="ariaLabel"
        aria-multiline="true"
        class="chat_message_textarea"
        @input="handleInput"
        @keydown="$emit('keydown', $event)"
        @paste="$emit('paste', $event)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @compositionstart="$emit('compositionstart', $event)"
        @compositionend="$emit('compositionend', $event)"
    />
</template>

<script>
export default {
    name: 'ChatMessageTextarea',

    props: {
        value: {
            type: String,
            default: ''
        },

        inputId: {
            type: String,
            default: ''
        },

        inputName: {
            type: String,
            default: 'message'
        },

        placeholder: {
            type: String,
            default: 'Type a message...'
        },

        autocomplete: {
            type: String,
            default: 'off'
        },

        ariaLabel: {
            type: String,
            default: 'Message'
        },

        rows: {
            type: Number,
            default: 1,
            validator(value) {
                return value > 0;
            }
        },

        maxLength: {
            type: [Number, String],
            default: null
        },

        spellcheck: {
            type: [Boolean, String],
            default: true
        },

        disabled: {
            type: Boolean,
            default: false
        },

        autoResize: {
            type: Boolean,
            default: true
        },

        minHeight: {
            type: Number,
            default: 24,
            validator(value) {
                return value > 0;
            }
        },

        maxHeight: {
            type: Number,
            default: 96,
            validator(value) {
                return value > 0;
            }
        },

        autofocus: {
            type: Boolean,
            default: false
        }
    },

    watch: {
        value() {
            this.scheduleResize();
        },

        disabled(isDisabled) {
            if (!isDisabled) {
                this.scheduleResize();
            }
        }
    },

    mounted() {
        // this.resizeTextarea();

        if (this.autofocus && !this.disabled) {
            this.focus();
        }
    },

    methods: {
        handleInput(event) {
            const nextValue = event && event.target
                ? event.target.value
                : '';

            this.$emit('input', nextValue, event);
            // this.resizeTextarea();
        },

        scheduleResize() {
            this.$nextTick(() => {
                // this.resizeTextarea();
            });
        },

        resizeTextarea() {
            const textarea = this.getInputElement();

            if (!textarea) {
                return;
            }

            if (!this.autoResize) {
                textarea.style.height = `${this.minHeight}px`;
                textarea.style.overflowY = 'auto';
                return;
            }

            textarea.style.height = 'auto';

            const nextHeight = Math.max(
                this.minHeight,
                Math.min(textarea.scrollHeight, this.maxHeight)
            );

            textarea.style.height = `${nextHeight}px`;
            textarea.style.overflowY =
                textarea.scrollHeight > this.maxHeight
                    ? 'auto'
                    : 'hidden';
        },

        focus() {
            this.$nextTick(() => {
                const textarea = this.getInputElement();

                if (textarea && !this.disabled) {
                    textarea.focus();
                }
            });
        },

        getInputElement() {
            return this.$refs.textarea || null;
        },

        setSelectionRange(start, end) {
            const textarea = this.getInputElement();

            if (
                textarea &&
                typeof textarea.setSelectionRange === 'function'
            ) {
                textarea.setSelectionRange(start, end);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.chat_message_textarea {
    display: block;
    width: 100%;
    min-width: 0;
    min-height: 35px;
    margin: 0;
    padding: 5px 4px;
    overflow-x: hidden;
    overflow-y: hidden;
    color: #f5f5f5;
    caret-color: #ffd400;
    background: transparent;
    border: 0;
    outline: 0;
    box-shadow: none;
    resize: none;
    font-family:'proxima-nova', 'Source Sans Pro', sans-serif;
    font-size: 14px;
    line-height: 1.3;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    scrollbar-width: thin;
    scrollbar-color: #3b3b3b transparent;

    &::placeholder {
        color: #8d8d8d;
        opacity: 1;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: #3b3b3b;
    }
}
</style>
